import {combineReducers, createStore} from "redux";
import {
    dialog,
    DialogState,
    setWorkspaceSetting,
    transform,
    workspaceSettings,
    WorkspaceSettingsState
} from "./workspace";
import {TypedUseSelectorHook, useDispatch, useSelector} from "react-redux";
import {nodes, NodesState, Unique} from "./nodes";
import {input, InputState, keyDown, keyUp} from "./input";
import {ZoomTranslateState} from "../components/workspace/ZoomTranslate";
import {code, CodeState} from "./code";
import {useEffect} from "react";
import {optimization, OptimizationState} from "./optimization";

const reducer = combineReducers({
    dialog, nodes, input, transform, code, workspaceSettings, optimization
});

export interface State {
    dialog: DialogState
    transform: ZoomTranslateState
    nodes: NodesState
    input: InputState
    code: CodeState
    workspaceSettings: WorkspaceSettingsState
    optimization: OptimizationState
}

export const configureStore = (initialState?: State) =>
    createStore(
        reducer,
        initialState,
        (window as any).__REDUX_DEVTOOLS_EXTENSION__ && (window as any).__REDUX_DEVTOOLS_EXTENSION__()
    );

const store = configureStore();

const handleDown = (event: KeyboardEvent) => store.dispatch(keyDown(event.key));
const handleUp = (event: KeyboardEvent) => store.dispatch(keyUp(event.key));

window.addEventListener('keydown', handleDown);
window.addEventListener('keyup', handleUp);

export default store;

export const useTypedSelector: TypedUseSelectorHook<State> = useSelector
export const useReduxState = <T>(type: string, selector: (state: State) => T): [T, (value: T) => void] => {
    let value: T = useTypedSelector(selector);
    let dispatch = useDispatch();
    let setValue = (payload: T) => dispatch({type, payload});
    return [value, setValue];
}
export const useSelectedNodes = (): [string[] | undefined, (value: string[] | undefined) => any] => useReduxState<string[] | undefined>('SELECTNODES', state => state.nodes.selected);

export const useKeyPressed = (key: string) => useTypedSelector(state => state.input.keyPressed[key]);

export const useWorkspaceSetting = (key: string) => useTypedSelector(state => state.workspaceSettings[key]);
export const useWorkspaceSettingOrDefault = <T>(key: string, d: T) => useTypedSelector(state => state.workspaceSettings[key] ?? d);
export const useWorkspaceSettingState = <T>(key: string) => {
    const wss = useWorkspaceSetting(key);
    const dispatch = useDispatch();
    const setWss = (value: T) => dispatch(setWorkspaceSetting(key, value));
    return [wss, setWss];
}
export const useWorkspaceSettingsUpdate = <T>(key: string, value: T) => {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(setWorkspaceSetting(key, value))
    }, [value]);
}